import { Box, Tooltip, type Theme } from "@mui/material";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";

export type IssueSeverity = "error" | "warning";

const FALLBACK: Record<IssueSeverity, { background: string; text: string; border: string }> = {
  error: { background: "#FFD9D2", text: "#DD3630", border: "#FF9A87" },
  warning: { background: "#FBFCE5", text: "#CE7210", border: "#FAFA64" },
};

function getRowColors(theme: Theme, severity: IssueSeverity) {
  return {
    background: theme.palette.statusChip?.[severity].background ?? FALLBACK[severity].background,
    text: theme.palette.statusChip?.[severity].text ?? FALLBACK[severity].text,
    border: theme.palette.border?.[severity] ?? FALLBACK[severity].border,
  };
}

interface IssueRowProps {
  severity: IssueSeverity;
  title: string;
  description: string;
}

/**
 * Compact issue row used in the multi-reason invite modals. Renders the
 * brief title inline; the full description is revealed via tooltip on hover
 * (and focus, for keyboard users). Colors come from the rebrand palette's
 * statusChip tokens so error rows render red and warning rows render orange.
 */
export function IssueRow(props: IssueRowProps) {
  const { severity, title, description } = props;

  return (
    <Tooltip arrow describeChild placement="top" title={description}>
      <Box
        tabIndex={0}
        sx={(theme: Theme) => {
          const colors = getRowColors(theme, severity);
          return {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(2),
            padding: theme.spacing(2, 3),
            backgroundColor: colors.background,
            border: `${theme.borderWidth?.regular} solid ${colors.border}`,
            borderRadius: theme.borderRadius?.medium,
            cursor: "help",
            outline: "none",
            "&:focus-visible": {
              boxShadow: `0 0 0 2px ${colors.text}`,
            },
          };
        }}
      >
        <Box
          sx={(theme: Theme) => ({
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            color: getRowColors(theme, severity).text,
          })}
        >
          <Icon size="small" type="warning" />
        </Box>
        <Text
          semibold
          sx={(theme: Theme) => ({ color: getRowColors(theme, severity).text })}
          variant="body2"
        >
          {title}
        </Text>
      </Box>
    </Tooltip>
  );
}
